/**
 * EntityLandingPage · /o/:slug
 *
 * Rediseño jul-2026 al estilo editorial claro Cines Alba/IES:
 * - Fondo claro con hero portada + logo + accent del organizador
 * - Sobre el organizador · introducción editorial
 * - Cartelera con posters grandes 3/4 estilo Cines Alba
 * - Bloque contacto (Web / Instagram) y footer
 */
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '../api/client';
import EntityThemeProvider from '../components/EntityThemeProvider';

interface EntityPublic {
  id: string;
  slug: string;
  name: string;
  description?: string | null;
  logo_url?: string | null;
  cover_url?: string | null;
  accent_color?: string | null;
  website_url?: string | null;
  instagram_url?: string | null;
  entity_type?: string | null;
  city?: string | null;
  province?: string | null;
}

interface Show {
  id: string;
  slug: string;
  title: string;
  subtitle?: string | null;
  poster_url?: string | null;
  event_datetime: string;
  venue_name?: string | null;
  venue_city?: string | null;
  ticket_price?: number | null;
  ticket_price_min?: number | null;
  ticket_price_max?: number | null;
  tickets_available?: number | null;
  status: string;
  category?: string | null;
}

const C = {
  bg: '#FFFFFF',
  soft: '#FAF7F4',
  cream: '#F5F0E8',
  ink: '#0A0A0A',
  muted: '#6B6660',
  faint: '#A8A29C',
  border: '#EAE6E1',
  fontDisplay: "'Archivo', 'Arial Narrow', sans-serif",
  fontBody: "'DM Sans', system-ui, sans-serif",
  fontMono: "'JetBrains Mono', monospace",
} as const;

const MESES = ['ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN', 'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'];

function fmtDay(iso: string) {
  const d = new Date(iso);
  return { day: String(d.getDate()).padStart(2, '0'), mes: MESES[d.getMonth()] };
}
function fmtLong(iso: string): string {
  return new Date(iso).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' });
}
function priceLabel(s: Show): string {
  if (s.ticket_price != null) return `${Number(s.ticket_price).toFixed(2)} €`;
  if (s.ticket_price_min != null && s.ticket_price_max != null && s.ticket_price_min !== s.ticket_price_max) {
    return `desde ${Number(s.ticket_price_min).toFixed(2)} €`;
  }
  if (s.ticket_price_min != null) return `${Number(s.ticket_price_min).toFixed(2)} €`;
  return 'A consultar';
}

export default function EntityLandingPage() {
  const { slug = '' } = useParams();
  const [entity, setEntity] = useState<EntityPublic | null>(null);
  const [shows, setShows] = useState<Show[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;
    document.title = 'Cargando… · Pagafácil';
    Promise.all([
      api.get<EntityPublic>(`/public/orgs/${slug}`),
      api.get<Show[]>(`/public/orgs/${slug}/shows`),
    ])
      .then(([e, s]) => {
        setEntity(e.data);
        setShows(s.data || []);
        document.title = `${e.data.name} · Pagafácil`;
      })
      .catch((e) => setError(e?.response?.data?.detail || 'Organizador no encontrado'))
      .finally(() => setLoading(false));
  }, [slug]);

  const brand = entity?.accent_color || '#B01820';

  if (loading) return <Shell brand={brand}><div style={{ padding: '160px 24px', textAlign: 'center', color: C.muted }}>Cargando…</div></Shell>;

  if (error || !entity) {
    return (
      <Shell brand={brand}>
        <div style={{ padding: '120px 24px', textAlign: 'center', maxWidth: 600, margin: '0 auto' }}>
          <div style={{ fontFamily: C.fontDisplay, fontSize: 11, letterSpacing: 6, textTransform: 'uppercase', color: brand, marginBottom: 16, fontWeight: 700 }}>
            404
          </div>
          <h1 style={{ fontFamily: C.fontDisplay, fontSize: 40, textTransform: 'uppercase', letterSpacing: 3, margin: '0 0 16px', fontWeight: 900 }}>
            Organizador no encontrado
          </h1>
          <p style={{ color: C.muted }}>{error}</p>
          <Link to="/entradas" style={{ display: 'inline-block', marginTop: 24, padding: '12px 32px', border: `2px solid ${brand}`, color: brand, textDecoration: 'none', fontFamily: C.fontDisplay, fontSize: 12, letterSpacing: 3, textTransform: 'uppercase', fontWeight: 700 }}>
            Ver cartelera
          </Link>
        </div>
      </Shell>
    );
  }

  const activeShows = shows.filter(s => s.status === 'on_sale' || s.status === 'sold_out');

  return (
    <Shell brand={brand} entityName={entity.name}>
      {/* ── HERO con cover + logo + info ── */}
      <section style={{
        position: 'relative', overflow: 'hidden',
        background: entity.cover_url ? `url(${entity.cover_url}) center/cover` : `linear-gradient(135deg, ${brand}, #1E2A2E)`,
        minHeight: 420,
        display: 'flex', alignItems: 'flex-end',
      }}>
        <div style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(180deg, rgba(10,10,10,0.15) 0%, rgba(10,10,10,0.85) 100%)',
        }} />
        <div style={{ position: 'relative', maxWidth: 1120, margin: '0 auto', padding: '60px 24px 40px', width: '100%' }}>
          <div style={{ display: 'flex', gap: 24, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            {entity.logo_url && (
              <img src={entity.logo_url} alt={entity.name}
                style={{
                  width: 110, height: 110, borderRadius: 14, objectFit: 'cover',
                  border: '3px solid #FFF', background: '#FFF',
                }}
              />
            )}
            <div style={{ flex: 1, minWidth: 260 }}>
              {entity.entity_type && (
                <div style={{
                  fontFamily: C.fontMono, fontSize: 11, letterSpacing: '0.18em',
                  textTransform: 'uppercase', color: '#FFF', opacity: 0.75, marginBottom: 6, fontWeight: 700,
                }}>
                  {entity.entity_type}
                </div>
              )}
              <h1 style={{
                fontFamily: C.fontDisplay, fontSize: 'clamp(36px, 5vw, 64px)',
                fontWeight: 900, lineHeight: 1, letterSpacing: '-0.02em',
                textTransform: 'uppercase', margin: 0, color: '#FFF',
              }}>
                {entity.name}
              </h1>
              {(entity.city || entity.province) && (
                <div style={{
                  fontFamily: C.fontMono, fontSize: 12, letterSpacing: '0.14em',
                  textTransform: 'uppercase', color: '#FFF', opacity: 0.85, marginTop: 12,
                }}>
                  📍 {[entity.city, entity.province].filter(Boolean).join(' · ')}
                </div>
              )}
            </div>
            {activeShows.length > 0 && (
              <a href="#cartelera" style={{
                padding: '14px 26px', background: brand, color: '#FFF',
                textDecoration: 'none', fontFamily: C.fontDisplay, fontSize: 13,
                fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase',
              }}>
                Ver cartelera →
              </a>
            )}
          </div>
        </div>
      </section>

      {/* ── Sobre el organizador ── */}
      {entity.description && (
        <section style={{ background: C.soft, borderBottom: `1px solid ${C.border}` }}>
          <div style={{ maxWidth: 720, margin: '0 auto', padding: '56px 24px', textAlign: 'center' }}>
            <div style={{
              fontFamily: C.fontMono, fontSize: 10, letterSpacing: '0.2em',
              textTransform: 'uppercase', color: brand, fontWeight: 700, marginBottom: 14,
            }}>
              Sobre {entity.name.split(' ').slice(0, 3).join(' ')}
            </div>
            <p style={{
              fontFamily: C.fontBody, fontSize: 17, lineHeight: 1.75,
              color: C.ink, margin: 0, whiteSpace: 'pre-wrap',
            }}>
              {entity.description}
            </p>
            <div style={{ width: 60, height: 3, background: brand, margin: '32px auto 0' }} />
          </div>
        </section>
      )}

      {/* ── Cartelera con posters grandes ── */}
      <section id="cartelera" style={{ padding: '72px 24px 40px' }}>
        <div style={{ maxWidth: 1120, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <div style={{
              fontFamily: C.fontMono, fontSize: 11, letterSpacing: '0.2em',
              textTransform: 'uppercase', color: brand, fontWeight: 700, marginBottom: 10,
            }}>
              Próximos eventos
            </div>
            <h2 style={{
              fontFamily: C.fontDisplay, fontSize: 'clamp(28px, 4vw, 44px)',
              fontWeight: 900, letterSpacing: '0.02em', textTransform: 'uppercase',
              margin: 0, color: C.ink,
            }}>
              Cartelera
            </h2>
          </div>

          {shows.length === 0 ? (
            <div style={{
              padding: 60, textAlign: 'center', border: `1px solid ${C.border}`,
              background: C.soft, color: C.muted,
            }}>
              <div style={{ fontFamily: C.fontDisplay, fontSize: 22, letterSpacing: 2, textTransform: 'uppercase', color: C.ink, marginBottom: 8, fontWeight: 800 }}>
                Sin eventos activos
              </div>
              <div style={{ fontFamily: C.fontBody }}>Vuelve pronto — se anuncian nuevos eventos cada semana.</div>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: 24,
            }}>
              {shows.map(s => <PosterCard key={s.id} show={s} brand={brand} />)}
            </div>
          )}
        </div>
      </section>

      {/* ── Contacto ── */}
      {(entity.website_url || entity.instagram_url) && (
        <section style={{
          padding: '64px 24px', background: C.ink, color: '#FFF',
        }}>
          <div style={{ maxWidth: 720, margin: '0 auto', textAlign: 'center' }}>
            <div style={{
              fontFamily: C.fontMono, fontSize: 11, letterSpacing: '0.2em',
              textTransform: 'uppercase', color: brand, fontWeight: 700, marginBottom: 14,
            }}>
              Síguenos
            </div>
            <h2 style={{
              fontFamily: C.fontDisplay, fontSize: 'clamp(24px, 3vw, 36px)',
              fontWeight: 900, letterSpacing: '0.08em', textTransform: 'uppercase',
              margin: '0 0 28px',
            }}>
              {entity.name}
            </h2>
            <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
              {entity.website_url && (
                <a href={entity.website_url} target="_blank" rel="noreferrer" style={contactBtn(brand)}>
                  Web ↗
                </a>
              )}
              {entity.instagram_url && (
                <a href={entity.instagram_url} target="_blank" rel="noreferrer" style={contactBtn(brand)}>
                  Instagram ↗
                </a>
              )}
            </div>
          </div>
        </section>
      )}

      <Footer accent={brand} entityName={entity.name} />
    </Shell>
  );
}

function PosterCard({ show, brand }: { show: Show; brand: string }) {
  const soldOut = show.status === 'sold_out' || (show.tickets_available != null && show.tickets_available <= 0);
  const cancelled = show.status === 'cancelled';
  const day = fmtDay(show.event_datetime);

  return (
    <Link
      to={`/e/${show.slug}`}
      className="pf-poster-card"
      style={{
        display: 'flex', flexDirection: 'column', textDecoration: 'none', color: 'inherit',
        background: '#FFF', border: `1px solid ${C.border}`, overflow: 'hidden',
        transition: 'all 0.3s ease', position: 'relative',
      }}
    >
      <div style={{
        aspectRatio: '3/4', position: 'relative',
        background: show.poster_url ? `url(${show.poster_url}) center/cover` : `linear-gradient(165deg,${brand},#1a0508)`,
      }}>
        {!show.poster_url && (
          <div style={{
            height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#FFF', fontFamily: C.fontDisplay, fontSize: 22, fontWeight: 900,
            letterSpacing: 2, textTransform: 'uppercase', textAlign: 'center', padding: 20,
          }}>
            {show.title}
          </div>
        )}
        {/* Badge fecha */}
        <div style={{
          position: 'absolute', top: 14, left: 14,
          background: '#FFF', padding: '6px 10px',
          fontFamily: C.fontDisplay, textAlign: 'center', lineHeight: 1,
        }}>
          <div style={{ fontSize: 20, fontWeight: 900, color: brand }}>{day.day}</div>
          <div style={{ fontSize: 9, color: C.muted, letterSpacing: 1.5, marginTop: 2 }}>{day.mes}</div>
        </div>
        {(cancelled || soldOut) && (
          <div style={{
            position: 'absolute', top: 14, right: 14,
            background: cancelled ? '#c0392b' : '#0a0a0a',
            color: '#FFF', padding: '6px 10px',
            fontFamily: C.fontMono, fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', fontWeight: 700,
          }}>
            {cancelled ? 'Cancelado' : 'Agotado'}
          </div>
        )}
      </div>

      <div style={{ padding: '18px 20px 20px' }}>
        {show.category && (
          <div style={{
            fontFamily: C.fontMono, fontSize: 10, letterSpacing: '0.15em',
            textTransform: 'uppercase', color: brand, fontWeight: 700, marginBottom: 6,
          }}>
            {show.category}
          </div>
        )}
        <div style={{
          fontFamily: C.fontDisplay, fontSize: 18, fontWeight: 800,
          letterSpacing: '0.02em', textTransform: 'uppercase', color: C.ink,
          lineHeight: 1.15, marginBottom: 8,
        }}>
          {show.title}
        </div>
        <div style={{ fontFamily: C.fontBody, fontSize: 12, color: C.muted, marginBottom: 12 }}>
          {fmtLong(show.event_datetime)}{show.venue_city ? ` · ${show.venue_city}` : ''}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 10, borderTop: `1px solid ${C.border}` }}>
          <span style={{ fontFamily: C.fontDisplay, fontSize: 16, fontWeight: 800, color: C.ink }}>
            {priceLabel(show)}
          </span>
          {!cancelled && !soldOut && (
            <span style={{
              fontFamily: C.fontMono, fontSize: 10, letterSpacing: 2,
              textTransform: 'uppercase', color: brand, fontWeight: 800,
            }}>
              Comprar →
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}

function Shell({ children, brand }: { children: React.ReactNode; brand: string; entityName?: string }) {
  return (
    <EntityThemeProvider accentColor={brand}>
      <div style={{ fontFamily: C.fontBody, color: C.ink, background: C.bg, minHeight: '100vh' }}>
        <Nav accent={brand} />
        {children}
        <style>{`
          .pf-poster-card:hover {
            transform: translateY(-4px);
            box-shadow: 0 20px 40px -20px rgba(0,0,0,0.2);
          }
        `}</style>
      </div>
    </EntityThemeProvider>
  );
}

function Nav({ accent }: { accent: string }) {
  return (
    <nav style={{
      position: 'sticky', top: 0, zIndex: 100,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '14px 24px', background: 'rgba(255,255,255,0.95)',
      backdropFilter: 'blur(10px)', borderBottom: `1px solid ${C.border}`,
    }}>
      <Link to="/" style={{
        display: 'flex', alignItems: 'center', gap: 8, color: C.ink, textDecoration: 'none',
        fontFamily: C.fontDisplay, fontSize: 14, fontWeight: 900,
        letterSpacing: '0.1em', textTransform: 'uppercase',
      }}>
        <span style={{ display: 'inline-block', width: 8, height: 8, background: accent, borderRadius: '50%' }} />
        Pagafácil
      </Link>
      <Link to="/entradas" style={{
        color: C.muted, textDecoration: 'none',
        fontFamily: C.fontMono, fontSize: 11,
        letterSpacing: '0.14em', textTransform: 'uppercase',
      }}>
        Cartelera
      </Link>
    </nav>
  );
}

function Footer({ accent, entityName }: { accent: string; entityName?: string }) {
  return (
    <footer style={{
      padding: '28px 24px', borderTop: `1px solid ${C.border}`, background: C.soft,
      display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between',
      gap: 12, fontSize: 12, fontFamily: C.fontMono, letterSpacing: '0.1em', color: C.muted,
      textTransform: 'uppercase',
    }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        <span style={{ width: 7, height: 7, background: accent, borderRadius: '50%' }} />
        {entityName ? `${entityName} · via Pagafácil` : 'Pagafácil'}
      </span>
      <a href="mailto:hola@pagafacil.eu" style={{ color: C.muted, textDecoration: 'none' }}>
        hola@pagafacil.eu
      </a>
      <Link to="/entradas" style={{ color: C.muted, textDecoration: 'none' }}>Cartelera</Link>
    </footer>
  );
}

const contactBtn = (color: string): React.CSSProperties => ({
  display: 'inline-block',
  padding: '12px 26px',
  background: color,
  color: '#FFF',
  textDecoration: 'none',
  fontFamily: "'Archivo', sans-serif",
  fontSize: 12, letterSpacing: '0.14em', textTransform: 'uppercase',
  fontWeight: 800,
});
